/**
 * Module-specific vendor-profile cards (view + edit) for VET, CAFE and
 * RESTAURANT vendors. These surface the details collected in the onboarding
 * "module details" step and let the vendor edit them after onboarding, reusing
 * the same save endpoints. Rendered by profile-page.tsx only for the modules a
 * vendor actually has.
 */
import { useState, type FormEvent } from 'react';
import { AlertTriangle, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TimePicker } from '@/components/ui/time-picker';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { TextField } from '@/components/text-field';
import { form_error_message } from '@/lib/forms';
import { ChipMultiSelect } from '@/components/chip-multi-select';
import { use_vet_details, use_save_vet_details } from '@/features/vet/hooks';
import {
  MEDICAL_DEGREE_OPTIONS,
  PET_SPECIES_OPTIONS as VET_SPECIES_OPTIONS,
  VET_SPECIALIZATION_OPTIONS,
  REGISTRATION_NUMBER_REGEX,
  type MedicalDegree,
  type PetSpecies as VetPetSpecies,
} from '@/features/vet/types';
import { use_cafe_details, use_save_cafe_details } from '@/features/cafe/hooks';
import { use_restaurant_details, use_save_restaurant_details } from '@/features/restaurant/hooks';
import {
  RESTAURANT_TYPE_OPTIONS,
  DELIVERY_RADIUS_OPTIONS,
  RESTAURANT_SERVICE_OPTIONS,
  PET_SPECIES_OPTIONS as REST_SPECIES_OPTIONS,
  FSSAI_REGEX,
  type RestaurantType,
  type DeliveryRadius,
  type RestaurantService,
  type PetSpecies as RestPetSpecies,
} from '@/features/restaurant/types';

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;
const to_minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** A labelled read-only row inside a view-mode card. */
function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm text-foreground">{value?.trim() ? value : '—'}</p>
    </div>
  );
}

/** Shared card header with an Edit affordance (hidden while editing). */
function EditHeader({
  title,
  editing,
  can_edit,
  on_edit,
  testid,
}: {
  title: string;
  editing: boolean;
  can_edit: boolean;
  on_edit: () => void;
  testid: string;
}) {
  return (
    <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
      <CardTitle className="text-lg">{title}</CardTitle>
      {!editing && can_edit && (
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={on_edit} data-testid={testid}>
          <Pencil className="h-4 w-4" /> Edit
        </Button>
      )}
    </CardHeader>
  );
}

/** A pill multi-select (chips) mirroring the service-area card. */
function ChipGroup<T extends string>({
  options,
  selected,
  on_toggle,
  testid_prefix,
}: {
  options: Array<{ value: T; label: string }>;
  selected: T[];
  on_toggle: (value: T) => void;
  testid_prefix: string;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => on_toggle(o.value)}
            aria-pressed={on}
            data-testid={`${testid_prefix}-${o.value}`}
            className={
              'rounded-full border-2 px-3 py-1 text-sm font-semibold transition-colors duration-fast ' +
              (on
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-brand-outline-variant/25 text-muted-foreground hover:bg-secondary')
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vet credentials
// ---------------------------------------------------------------------------

type VetForm = {
  vet_full_name: string;
  medical_degree: MedicalDegree | '';
  years_of_experience: string;
  registration_number: string;
  session_fee_inr: string;
  pet_types_treated: VetPetSpecies[];
  specialization_codes: string[];
};

export function VetDetailsCard() {
  const saved_q = use_vet_details();
  const save_m = use_save_vet_details();
  const saved = saved_q.data;

  const [editing, set_editing] = useState(false);
  const [form, set_form] = useState<VetForm | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const [reverify, set_reverify] = useState(false);

  function begin_edit() {
    if (!saved) return;
    set_form({
      vet_full_name: saved.vet_full_name,
      medical_degree: saved.medical_degree ?? '',
      years_of_experience: String(saved.years_of_experience),
      registration_number: saved.registration_number,
      session_fee_inr: saved.session_fee_inr,
      pet_types_treated: saved.pet_types_treated,
      specialization_codes: saved.specialization_codes,
    });
    set_error(null);
    set_editing(true);
  }

  function update<K extends keyof VetForm>(key: K, value: VetForm[K]) {
    set_form((f) => (f ? { ...f, [key]: value } : f));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    set_error(null);
    const years = Number(form.years_of_experience);
    const fee = Number(form.session_fee_inr);
    if (form.vet_full_name.trim().length < 2) return set_error('Enter the full name (min 2 characters).');
    if (!Number.isInteger(years) || years < 0 || years > 80) return set_error('Years of experience must be 0–80.');
    if (!REGISTRATION_NUMBER_REGEX.test(form.registration_number.trim()) || form.registration_number.trim().length < 5)
      return set_error('Enter a valid registration number (5–60 characters).');
    if (!(fee > 0) || fee > 100000) return set_error('Enter a session fee between 1 and 100000.');
    if (form.pet_types_treated.length < 1) return set_error('Pick at least one species treated.');
    if (form.specialization_codes.length < 1) return set_error('Pick at least one specialization.');
    // Degree is required since the 9 Oct 2026 client change; changing it after approval sends the profile back to review.
    if (!form.medical_degree) return set_error('Select your degree.');
    try {
      const res = await save_m.mutateAsync({
        vet_full_name: form.vet_full_name.trim(),
        medical_degree: form.medical_degree || undefined,
        years_of_experience: years,
        registration_number: form.registration_number.trim(),
        session_fee_inr: fee,
        pet_types_treated: form.pet_types_treated,
        specialization_codes: form.specialization_codes,
      });
      set_reverify(res.reverification_required === true);
      set_editing(false);
    } catch (err) {
      set_error(form_error_message(err, 'Could not save vet details.'));
    }
  }

  const degree_label = saved?.medical_degree
    ? (MEDICAL_DEGREE_OPTIONS.find((o) => o.value === saved.medical_degree)?.label ?? saved.medical_degree)
    : null;
  const spec_label = (code: string) =>
    VET_SPECIALIZATION_OPTIONS.find((o) => o.code === code)?.display_name ?? code;

  return (
    <Card data-testid="profile-vet-card">
      <EditHeader title="Vet credentials" editing={editing} can_edit={!!saved} on_edit={begin_edit} testid="edit-vet" />
      <CardContent className="flex flex-col gap-4">
        {reverify && !editing && (
          <div
            role="status"
            data-testid="vet-reverification-notice"
            className="flex items-start gap-2 rounded-card border-2 border-warning bg-card p-3 text-sm font-medium text-foreground"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p>
              You changed your degree or registration number, so your profile is back in review.
              Customers can't book you until an admin re-verifies it.
            </p>
          </div>
        )}
        {!saved ? (
          <p className="text-sm text-muted-foreground">No vet details on file yet.</p>
        ) : !editing || !form ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Vet name" value={saved.vet_full_name} />
            <Field label="Degree" value={degree_label} />
            <Field label="Experience" value={`${saved.years_of_experience} yrs`} />
            <Field label="Registration no." value={saved.registration_number} />
            <Field label="Session fee" value={`₹${saved.session_fee_inr}`} />
            <div className="sm:col-span-2">
              <Field
                label="Species treated"
                value={saved.pet_types_treated
                  .map((s) => VET_SPECIES_OPTIONS.find((o) => o.value === s)?.label ?? s)
                  .join(', ')}
              />
            </div>
            <div className="sm:col-span-2">
              <Field label="Specializations" value={saved.specialization_codes.map(spec_label).join(', ')} />
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-5">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TextField
                label="Vet full name"
                name="vet_full_name"
                value={form.vet_full_name}
                onChange={(e) => update('vet_full_name', e.target.value)}
                maxLength={120}
                data-testid="vet-name"
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="vet-degree">Degree</Label>
                <SelectField
                  id="vet-degree"
                  data-testid="vet-degree"
                  value={form.medical_degree}
                  placeholder="Choose a degree"
                  onValueChange={(v) => update('medical_degree', v as MedicalDegree)}
                  options={MEDICAL_DEGREE_OPTIONS}
                />
              </div>
              <TextField
                label="Years of experience"
                name="years_of_experience"
                inputMode="numeric"
                value={form.years_of_experience}
                onChange={(e) => update('years_of_experience', e.target.value.replace(/\D/g, '').slice(0, 2))}
                data-testid="vet-experience"
              />
              <TextField
                label="Registration number"
                name="registration_number"
                value={form.registration_number}
                onChange={(e) => update('registration_number', e.target.value)}
                maxLength={60}
                data-testid="vet-registration"
              />
              <TextField
                label="Session fee (₹)"
                name="session_fee_inr"
                inputMode="numeric"
                value={form.session_fee_inr}
                onChange={(e) => update('session_fee_inr', e.target.value.replace(/[^\d.]/g, ''))}
                data-testid="vet-fee"
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Species treated</p>
              <ChipGroup
                options={VET_SPECIES_OPTIONS}
                selected={form.pet_types_treated}
                on_toggle={(v) =>
                  update(
                    'pet_types_treated',
                    form.pet_types_treated.includes(v)
                      ? form.pet_types_treated.filter((x) => x !== v)
                      : [...form.pet_types_treated, v],
                  )
                }
                testid_prefix="vet-species"
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Specializations</p>
              <ChipGroup
                options={VET_SPECIALIZATION_OPTIONS.map((o) => ({ value: o.code, label: o.display_name }))}
                selected={form.specialization_codes}
                on_toggle={(v) =>
                  update(
                    'specialization_codes',
                    form.specialization_codes.includes(v)
                      ? form.specialization_codes.filter((x) => x !== v)
                      : [...form.specialization_codes, v],
                  )
                }
                testid_prefix="vet-spec"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" variant="gradient" disabled={save_m.isPending} data-testid="save-vet">
                {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Café details (hours)
// ---------------------------------------------------------------------------

export function CafeDetailsCard() {
  const saved_q = use_cafe_details();
  const save_m = use_save_cafe_details();
  const saved = saved_q.data;

  const [editing, set_editing] = useState(false);
  const [opening, set_opening] = useState('09:00');
  const [closing, set_closing] = useState('22:00');
  const [years, set_years] = useState('');
  const [error, set_error] = useState<string | null>(null);

  function begin_edit() {
    if (!saved) return;
    set_opening(saved.opening_time);
    set_closing(saved.closing_time);
    set_years(saved.years_active !== null ? String(saved.years_active) : '');
    set_error(null);
    set_editing(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    set_error(null);
    if (!HH_MM.test(opening) || !HH_MM.test(closing)) return set_error('Enter valid opening/closing times.');
    if (to_minutes(opening) >= to_minutes(closing)) return set_error('Opening must be before closing.');
    let years_active: number | undefined;
    if (years.trim() !== '') {
      const n = Number(years);
      if (!Number.isInteger(n) || n < 0 || n > 100) return set_error('Years active must be 0–100.');
      years_active = n;
    }
    try {
      await save_m.mutateAsync({ opening_time: opening, closing_time: closing, years_active });
      set_editing(false);
    } catch (err) {
      set_error(form_error_message(err, 'Could not save café details.'));
    }
  }

  return (
    <Card data-testid="profile-cafe-card">
      <EditHeader title="Café details" editing={editing} can_edit={!!saved} on_edit={begin_edit} testid="edit-cafe" />
      <CardContent>
        {!saved ? (
          <p className="text-sm text-muted-foreground">No café details on file yet.</p>
        ) : !editing ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Opening time" value={saved.opening_time} />
            <Field label="Closing time" value={saved.closing_time} />
            <Field label="Years active" value={saved.years_active !== null ? String(saved.years_active) : null} />
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-5">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cafe-opening">Opening time</Label>
                <TimePicker
                  id="cafe-opening"
                  value={opening}
                  onChange={(v) => set_opening(v)}
                  data-testid="cafe-opening"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cafe-closing">Closing time</Label>
                <TimePicker
                  id="cafe-closing"
                  value={closing}
                  onChange={(v) => set_closing(v)}
                  data-testid="cafe-closing"
                />
              </div>
            </div>
            <TextField
              label="Years active"
              name="years_active"
              optional
              inputMode="numeric"
              value={years}
              onChange={(e) => set_years(e.target.value.replace(/\D/g, '').slice(0, 3))}
              data-testid="cafe-years"
            />
            <div className="flex items-center gap-3">
              <Button type="submit" variant="gradient" disabled={save_m.isPending} data-testid="save-cafe">
                {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Restaurant details
// ---------------------------------------------------------------------------

type RestForm = {
  restaurant_type: RestaurantType[];
  delivery_radius: DeliveryRadius | '';
  fssai_license_number: string;
  pet_friendly_species: RestPetSpecies[];
  services_offered: RestaurantService[];
  opening_time: string;
  closing_time: string;
};

export function RestaurantDetailsCard() {
  const saved_q = use_restaurant_details();
  const save_m = use_save_restaurant_details();
  const saved = saved_q.data;

  const [editing, set_editing] = useState(false);
  const [form, set_form] = useState<RestForm | null>(null);
  const [error, set_error] = useState<string | null>(null);

  function begin_edit() {
    if (!saved) return;
    set_form({
      restaurant_type: saved.restaurant_type ?? [],
      delivery_radius: saved.delivery_radius ?? '',
      fssai_license_number: saved.fssai_license_number ?? '',
      pet_friendly_species: saved.pet_friendly_species,
      services_offered: saved.services_offered,
      opening_time: saved.opening_time,
      closing_time: saved.closing_time,
    });
    set_error(null);
    set_editing(true);
  }

  function update<K extends keyof RestForm>(key: K, value: RestForm[K]) {
    set_form((f) => (f ? { ...f, [key]: value } : f));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    set_error(null);
    if (!HH_MM.test(form.opening_time) || !HH_MM.test(form.closing_time))
      return set_error('Enter valid opening/closing times.');
    if (to_minutes(form.opening_time) >= to_minutes(form.closing_time))
      return set_error('Opening must be before closing.');
    const fssai = form.fssai_license_number.trim();
    if (fssai !== '' && !FSSAI_REGEX.test(fssai)) return set_error('FSSAI licence must be 14 digits, or leave blank.');
    if (form.pet_friendly_species.length < 1) return set_error('Pick at least one pet-friendly species.');
    if (form.services_offered.length < 1) return set_error('Pick at least one service offered.');
    try {
      await save_m.mutateAsync({
        // Always sent: clearing every type has to actually clear it.
        restaurant_type: form.restaurant_type,
        delivery_radius: form.delivery_radius || undefined,
        fssai_license_number: fssai || undefined,
        pet_friendly_species: form.pet_friendly_species,
        services_offered: form.services_offered,
        opening_time: form.opening_time,
        closing_time: form.closing_time,
      });
      set_editing(false);
    } catch (err) {
      set_error(form_error_message(err, 'Could not save restaurant details.'));
    }
  }

  const type_label =
    saved?.restaurant_type && saved.restaurant_type.length > 0
      ? saved.restaurant_type
          .map((t) => RESTAURANT_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t)
          .join(' · ')
      : null;
  const radius_label = saved?.delivery_radius
    ? (DELIVERY_RADIUS_OPTIONS.find((o) => o.value === saved.delivery_radius)?.label ?? saved.delivery_radius)
    : null;

  return (
    <Card data-testid="profile-restaurant-card">
      <EditHeader
        title="Restaurant details"
        editing={editing}
        can_edit={!!saved}
        on_edit={begin_edit}
        testid="edit-restaurant"
      />
      <CardContent>
        {!saved ? (
          <p className="text-sm text-muted-foreground">No restaurant details on file yet.</p>
        ) : !editing || !form ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Type" value={type_label} />
            <Field label="Delivery radius" value={radius_label} />
            <Field label="FSSAI licence" value={saved.fssai_license_number} />
            <Field label="Hours" value={`${saved.opening_time} – ${saved.closing_time}`} />
            <div className="sm:col-span-2">
              <Field
                label="Pet-friendly species"
                value={saved.pet_friendly_species
                  .map((s) => REST_SPECIES_OPTIONS.find((o) => o.value === s)?.label ?? s)
                  .join(', ')}
              />
            </div>
            <div className="sm:col-span-2">
              <Field
                label="Services offered"
                value={saved.services_offered
                  .map((s) => RESTAURANT_SERVICE_OPTIONS.find((o) => o.value === s)?.label ?? s)
                  .join(', ')}
              />
            </div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-5">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <ChipMultiSelect
                  label="Type"
                  hint="optional — pick any that apply"
                  testid="rest-type"
                  options={RESTAURANT_TYPE_OPTIONS}
                  selected={form.restaurant_type}
                  on_toggle={(v) =>
                    update(
                      'restaurant_type',
                      form.restaurant_type.includes(v)
                        ? form.restaurant_type.filter((x) => x !== v)
                        : [...form.restaurant_type, v],
                    )
                  }
                  disabled={save_m.isPending}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rest-radius">Delivery radius (optional)</Label>
                <SelectField
                  id="rest-radius"
                  data-testid="rest-radius"
                  value={form.delivery_radius}
                  placeholder="Choose a radius"
                  onValueChange={(v) => update('delivery_radius', v as DeliveryRadius)}
                  options={DELIVERY_RADIUS_OPTIONS}
                />
              </div>
              <TextField
                label="FSSAI licence number"
                name="fssai_license_number"
                optional
                inputMode="numeric"
                value={form.fssai_license_number}
                onChange={(e) => update('fssai_license_number', e.target.value.replace(/\D/g, '').slice(0, 14))}
                data-testid="rest-fssai"
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rest-opening">Opening time</Label>
                <TimePicker
                  id="rest-opening"
                  value={form.opening_time}
                  onChange={(v) => update('opening_time', v)}
                  data-testid="rest-opening"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rest-closing">Closing time</Label>
                <TimePicker
                  id="rest-closing"
                  value={form.closing_time}
                  onChange={(v) => update('closing_time', v)}
                  data-testid="rest-closing"
                />
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Pet-friendly species</p>
              <ChipGroup
                options={REST_SPECIES_OPTIONS}
                selected={form.pet_friendly_species}
                on_toggle={(v) =>
                  update(
                    'pet_friendly_species',
                    form.pet_friendly_species.includes(v)
                      ? form.pet_friendly_species.filter((x) => x !== v)
                      : [...form.pet_friendly_species, v],
                  )
                }
                testid_prefix="rest-species"
              />
            </div>
            <div>
              <p className="mb-1.5 text-sm font-medium">Services offered</p>
              <ChipGroup
                options={RESTAURANT_SERVICE_OPTIONS}
                selected={form.services_offered}
                on_toggle={(v) =>
                  update(
                    'services_offered',
                    form.services_offered.includes(v)
                      ? form.services_offered.filter((x) => x !== v)
                      : [...form.services_offered, v],
                  )
                }
                testid_prefix="rest-service"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" variant="gradient" disabled={save_m.isPending} data-testid="save-restaurant">
                {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => set_editing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
